/**
 * Health plan picker for program setup (TRUS-725).
 *
 * The plan is chosen ONCE, on the program. MIF import, scheduled reports and the
 * billing-rule lookups read it from the program instead of guessing (the MIF wizard
 * used to hard-code SFHP for every org). Lists the plans for the org's state
 * (default CA), Medi-Cal FFS included, from GET /api/programs/payers.
 */
import React, { useEffect, useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { programsAPI, ProgramPayer } from '@/services/api/programs';

interface HealthPlanPickerProps {
  id: string;
  value: number | null | undefined;
  onChange: (payerId: number | null) => void;
  className?: string;
}

export function HealthPlanPicker({ id, value, onChange, className }: HealthPlanPickerProps) {
  const { t } = useAppTranslation();
  const [payers, setPayers] = useState<ProgramPayer[]>([]);
  const [state, setState] = useState<string>('');
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    programsAPI
      .listPayers()
      .then((res) => {
        if (cancelled) return;
        setPayers(res.payers || []);
        setState(res.state || '');
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={className}>
      <label htmlFor={id} className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {t('programPayer.label', 'Health plan')}
      </label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
      >
        <option value="">{t('programPayer.none', 'No health plan')}</option>
        {payers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.payer_type === 'medicaid_ffs'
              ? t('programPayer.ffsOption', '{{name}} (fee-for-service)', { name: p.payer_name })
              : p.payer_name}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500">
        {loadFailed
          ? t('programPayer.loadFailed', 'Health plans could not be loaded. You can choose one later.')
          : t(
              'programPayer.help',
              'The plan this program bills, from plans in {{state}}. Imports and reports use it, so choose it once here.',
              { state: state || 'CA' }
            )}
      </p>
    </div>
  );
}

export default HealthPlanPicker;
